import { useState } from 'react';
import type { ApiClientErrorCode, Furniture } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Lightbox } from '@/components/Lightbox/Lightbox';
import { ForwardSheet } from '@/components/ForwardSheet/ForwardSheet';
import { buildCardShareUrl } from '@/lib/shareUrl';

/**
 * Presenter Экрана 2.5.8 (Карточка фурнитуры). Блок 5, ADR-0068, ADR-0005.
 *
 * Минимизированная карточка (решение Aibek): только идентификация товара +
 * действия, без «Описания» и «Характеристик».
 *   hero(+fallback) → name → «Бренд: {brandName}» →
 *   2 вертикальные кнопки (P-019): «Найти в магазинах», «Переслать клиенту».
 *
 * Поля `description` / `specs` / `subcategory` остаются в контракте `Furniture`
 * (контракт не трогаем), но НЕ рендерятся.
 *
 * Бренд — ГИБРИД (решение Aibek): показываем читаемое `brandName` (а НЕ код `brand`,
 * как в тексте прототипа). Это единственное намеренное расхождение с прототипом
 * (так же сделано в списке E5). Матч магазинов по коду — серверно (E7).
 *
 * ForwardSheet — общий shared-компонент (`@/components/ForwardSheet`, дедуп B-222):
 * текст-агностичен, call-site отдаёт `message` (без ссылки) + `url` раздельно.
 * Формат сообщения у фурнитуры собственный (без noun, в отличие от decor) — см.
 * `forwardMessage` ниже; ссылку (`buildCardShareUrl`) ForwardSheet добавляет сам.
 *
 * 4 состояния (P-021): loading / success / not-found / error.
 * Hero-зум (Lightbox): при наличии реального фото тап → полноэкранный просмотр
 * (зеркало DecorCardView/HandleCardView, паритет). Fallback-иконка не зумится.
 */
export type FurnitureCardState = 'loading' | 'success' | 'not-found' | 'error';

export interface FurnitureCardViewProps {
  state: FurnitureCardState;
  furniture?: Furniture;
  /**
   * Код/сообщение ошибки. Намеренно НЕ рендерится: ErrorState показывает
   * фиксированный текст (design-system ui-states), серверный `message` не
   * выводим пользователю (P-009, в отличие от decor-карточки — там он
   * показан и является потенциальной leak-поверхностью). Оставлен в контракте
   * для паритета с `DecorCardView` и возможной телеметрии.
   */
  error?: { code: ApiClientErrorCode; message: string };
  /** Signed-URL hero-фото (по imageFilename). null/undefined или onError → fallback. */
  heroImageUrl?: string | null;
  /** «Скопировать сообщение» в шите пересылки — clipboard в Container. */
  onCopyMessage: (text: string) => void;
  /** «Найти в магазинах» → /furniture/:globalId/shops (роут E7). */
  onFindShops: () => void;
  onBackToSearch: () => void;
}

/**
 * Сообщение «Переслать клиенту» БЕЗ ссылки — формат прототипа:
 * «{name}, {brandName}, смотрите по ссылке». Бренд-гибрид → brandName. Ссылку
 * карточки (`buildCardShareUrl('furniture', …)`) ForwardSheet добавляет сам:
 * инлайн в WhatsApp/MAX/копирование, в `url=` для Telegram.
 */
function forwardMessage(furniture: Furniture): string {
  return `${furniture.name}, ${furniture.brandName}, смотрите по ссылке`;
}

const ACTION_BTN_CLASS = [
  'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold',
  'text-[16px] py-3 px-4 flex items-center gap-3',
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
].join(' ');

/** Иконка «нет фото» (picture-frame), currentColor = text-ink. */
function ImagePlaceholderIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

export function FurnitureCardView(props: FurnitureCardViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Фурнитура" />
      <section className="flex-1 p-6 overflow-y-auto">
        {props.state === 'loading' && <LoadingState />}
        {props.state === 'success' && props.furniture && (
          <SuccessState
            furniture={props.furniture}
            heroImageUrl={props.heroImageUrl}
            onCopyMessage={props.onCopyMessage}
            onFindShops={props.onFindShops}
          />
        )}
        {props.state === 'not-found' && <NotFoundState onBackToSearch={props.onBackToSearch} />}
        {props.state === 'error' && <ErrorState onBackToSearch={props.onBackToSearch} />}
      </section>
      <BottomBar />
    </main>
  );
}

function SuccessState({
  furniture,
  heroImageUrl,
  onCopyMessage,
  onFindShops,
}: {
  furniture: Furniture;
  heroImageUrl?: string | null;
  onCopyMessage: (text: string) => void;
  onFindShops: () => void;
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [heroImgFailed, setHeroImgFailed] = useState(false);
  // Зум фото — локальное состояние экрана (P-036): закрывается тапом/крестиком/
  // Esc внутри Lightbox, НЕ через bottom-bar «Назад» (та ведёт на пред. экран).
  const [zoomOpen, setZoomOpen] = useState(false);
  const showHeroImage = Boolean(heroImageUrl) && !heroImgFailed;

  return (
    <div className="flex flex-col gap-5">
      {/* Hero: реальное фото R2 (signed URL), fallback — иконка + название (прототип).
          При наличии фото — тап открывает полноэкранный зум (Lightbox); fallback не зумится. */}
      {showHeroImage ? (
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          aria-label={`Открыть фото на весь экран: ${furniture.name}`}
          className="block w-full aspect-video rounded-xl border-[1.5px] border-accent overflow-hidden bg-card p-0 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
        >
          <img
            src={heroImageUrl as string}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setHeroImgFailed(true)}
          />
        </button>
      ) : (
        <div
          aria-label="Нет фото"
          className="w-full aspect-video rounded-xl border-[1.5px] border-accent bg-card flex flex-col items-center justify-center gap-2 text-ink p-4 text-center"
        >
          <ImagePlaceholderIcon />
          <span className="text-base leading-snug">{furniture.name}</span>
        </div>
      )}
      {showHeroImage && zoomOpen && (
        <Lightbox
          src={heroImageUrl as string}
          alt={furniture.name}
          onClose={() => setZoomOpen(false)}
        />
      )}

      {/* Name + бренд (brandName, гибрид). Global_ID пользователю НЕ показываем (P-042). */}
      <div className="flex flex-col gap-2">
        <h2 className="font-display text-h2 text-ink m-0">{furniture.name}</h2>
        <p className="text-muted text-base m-0">
          Бренд: <span className="text-ink font-semibold">{furniture.brandName}</span>
        </p>
      </div>

      {/* 2 вертикальные кнопки (P-019). */}
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onFindShops}
          aria-label="Найти в магазинах"
          className={ACTION_BTN_CLASS}
        >
          <span className="flex-1 text-left">Найти в магазинах</span>
          <span aria-hidden="true" className="text-ink">
            ›
          </span>
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label="Переслать клиенту"
          className={ACTION_BTN_CLASS}
        >
          <span className="flex-1 text-left">Переслать клиенту</span>
        </button>
      </div>

      <ForwardSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        message={forwardMessage(furniture)}
        url={buildCardShareUrl('furniture', {
          globalId: furniture.globalId,
          shortId: furniture.shortId,
        })}
        onCopyMessage={onCopyMessage}
      />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function NotFoundState({ onBackToSearch }: { onBackToSearch: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">Товар не найден</p>
      <button
        type="button"
        onClick={onBackToSearch}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад к поиску
      </button>
    </div>
  );
}

function ErrorState({ onBackToSearch }: { onBackToSearch: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      <button
        type="button"
        onClick={onBackToSearch}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад к поиску
      </button>
    </div>
  );
}
